import { Outlet, Link, useLocation } from 'react-router-dom';
import { Home, Wrench, Calendar, Droplets, Bell, Users, LogOut, Menu, Gauge, Sun, Snowflake, Palmtree, Clock, Video } from 'lucide-react';
import { useState } from 'react';

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Navigation items filtered by user role
  let navigation = [];
  
  if (user?.role === 'water_monitor') {
    // Water monitor can only see Water Monitoring page
    navigation = [
      { name: 'Monitoraggio Acqua', href: '/water-monitoring', icon: Droplets },
    ];
  } else {
    // All other roles see full menu
    navigation = [
      { name: 'Dashboard', href: '/', icon: Home },
      { name: 'Impianti', href: '/equipment', icon: Wrench },
      { name: 'Manutenzioni', href: '/maintenance', icon: Wrench },
      { name: 'Pianificazione', href: '/scheduled', icon: Calendar },
      { name: 'Monitoraggio Acqua', href: '/water-monitoring', icon: Droplets },
      { name: 'Contatori Acqua', href: '/water-meters', icon: Gauge },
      { name: 'Fotovoltaico', href: '/solar-readings', icon: Sun },
      { name: 'Lavori Natale', href: '/christmas-works', icon: Snowflake },
      { name: 'Lavori Estate', href: '/summer-works', icon: Palmtree },
      { name: 'Orari Riscaldamento', href: '/uta-schedules', icon: Clock },
      { name: 'Alert', href: '/alerts', icon: Bell },
    ];
    
    // Add Video Instructions for admin and maintainer only
    if (user?.role === 'admin' || user?.role === 'maintainer') {
      navigation.push({ name: 'Video Istruzioni', href: '/video-instructions', icon: Video });
    }
    
    if (user?.role === 'admin') {
      navigation.push({ name: 'Utenti', href: '/users', icon: Users });
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg bg-white shadow-lg text-gray-700 hover:bg-gray-50"
          data-testid="mobile-menu-toggle"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-gray-100 flex-shrink-0">
            <h1 className="text-2xl font-bold gradient-text" data-testid="app-title">Piave Nuoto</h1>
            <p className="text-sm text-gray-500 mt-1">Gestione Manutenzione</p>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                      : 'text-gray-700 hover:bg-blue-50'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-gray-900" data-testid="user-name">{user.name}</p>
                <p className="text-xs text-gray-500" data-testid="user-role">{user.role}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;